import styled from '@emotion/styled';
import type { TagEntry } from '@racemap/sdk/schema/starters';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { NO_MEMBER_TAG_VALUE } from '@racemap/utilities/consts/starters';
import type {
  PreparedRanksPackage,
  PreparedStarterInfo,
  TagsCollection,
} from '@racemap/utilities/functions/timing';
import { isGroupEvent } from '@racemap/utilities/functions/utils';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { type Immutable, produce } from 'immer';
import type { Column } from '../TableBody';
import { type Sorting, TableHeaderCellBody } from '../TableHeaderCell';
import { DoubleCell } from './BasicCell';

export function getSelectedTagKeys(selectedTags: TagsCollection): Array<string> {
  return Array.from(selectedTags.entries())
    .filter(([_k, v]) => v.length > 0)
    .map(([k]) => k);
}

export function areTagsSelected(selectedTags: TagsCollection): boolean {
  return getSelectedTagKeys(selectedTags).length > 0;
}

export function getFilteredTags(
  tags: Immutable<TagsCollection>,
  event: Immutable<RacemapEvent>,
): Immutable<TagsCollection> {
  const config = event.playerOptions.tagCategoriesConfig;
  if (Object.keys(config).length === 0) return tags;

  const filteredTags: Map<string, Immutable<Array<TagEntry>>> = new Map();
  tags.forEach((pT, tK) => {
    if (config[tK]?.visible ?? true) filteredTags.set(tK, pT);
  });
  return filteredTags;
}

export function appendNoneTags(
  ranksPackage: Immutable<PreparedRanksPackage>,
  possibleTags: Immutable<TagsCollection>,
): Immutable<PreparedRanksPackage> {
  return produce(ranksPackage, (rP) => {
    for (const starter of rP.starters) {
      for (const tagKey of possibleTags.keys()) {
        if (starter.tags != null && starter.tags[tagKey] == null)
          starter.tags[tagKey] = NO_MEMBER_TAG_VALUE;
      }
    }
  });
}

function TagColComponent({
  starterInfo,
  tagKey,
  tagStarterCount,
}: {
  starterInfo: Immutable<PreparedStarterInfo>;
  tagKey: string;
  tagStarterCount?: Immutable<Map<string, number>>;
}) {
  const tagValue = starterInfo.tags != null ? starterInfo.tags[tagKey] : null;
  const tagsRank = starterInfo.tagsRank != null ? starterInfo.tagsRank[tagKey] : null;

  if (tagValue == null || tagValue === NO_MEMBER_TAG_VALUE) return <Cell />;
  return (
    <Cell title={`${tagKey}: ${tagValue}`}>
      <RankAndGroup>
        {tagsRank != null && (
          <>
            <Rank>{tagsRank}</Rank>{' '}
            <StarterOfGroup>({tagStarterCount?.get(tagValue)})</StarterOfGroup>
          </>
        )}
      </RankAndGroup>
      <Text>{tagValue}</Text>
    </Cell>
  );
}

export function getTagColumns(
  tags: Immutable<TagsCollection>,
  event: Immutable<RacemapEvent>,
  tagsStarterCount?: Immutable<PreparedRanksPackage>['tagsStarterCount'],
): Array<Column> {
  const keys = Array.from(tags.keys()).filter((k) => k !== 'State');
  const isGroup = isGroupEvent(event);

  return keys.map((t, i) => ({
    key: `tag_${t}`,
    title: t,
    width: 85,
    render: function renderer(row: Immutable<PreparedStarterInfo>) {
      return (
        <TagColComponent starterInfo={row} tagKey={t} tagStarterCount={tagsStarterCount?.get(t)} />
      );
    },
    headerRender: function renderer(column: Column, sorting: Sorting | null) {
      return (
        <HeaderCell withRightBorder={i === keys.length - 1} column={column} sorting={sorting} />
      );
    },
    sorter: (a: Immutable<PreparedStarterInfo>, b: Immutable<PreparedStarterInfo>) => {
      if (isGroup) return a.tags?.[t].localeCompare(b.tags?.[t] || '') || -1;
      if (a.tagsRank == null || a.tagsRank[t] == null) return 1;
      if (b.tagsRank == null || b.tagsRank[t] == null) return -1;

      return a.tagsRank[t] - b.tagsRank[t];
    },
  }));
}

const RankAndGroup = styled.div`
  display: flex;
`;

const Rank = styled.div`
  font-weight: 600;
  margin-right: 5px;
`;

const StarterOfGroup = styled.div`
  color: ${RacemapColors.DarkGray};
`;

const Cell = styled(DoubleCell)`
  border-right: solid 1px ${RacemapColors.LightGray};
  align-items: center;
  padding: 2px;
`;

const Text = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  text-align: center;
  color: ${RacemapColors.DarkGray};
`;

const HeaderCell = styled(TableHeaderCellBody)<{ withRightBorder: boolean }>`
  justify-content: center;
  font-weight: 600;
  border-right: ${(props) =>
    props.withRightBorder ? `solid 1px ${RacemapColors.LightGray}` : 'unset'};
`;
